import React, { useCallback, useState, useLayoutEffect, useEffect, useRef } from 'react';
import { Slider } from '../layer/Slider';
import { DomEvent } from '../layer/dialogHelper';
import classNames from 'classnames';
import { SliderHeader } from './SliderHeader';
import { generateGuid } from 'utils/Guid';
import './PortalSlider.less';
import { getMaxZIndex } from 'utils/DomUtils';
import { ensureTabManagement, TabManagementUtil } from 'utils/TabManagementUtil';

export const SLIDER_CLASS = `dpc-portal-slider`;
export const CLOSE_EVENT_PREFIX = `close-slider__`;
export const TOGGLE_EVENT_PREFIX = `toggle-slider__`;
export const SETTITLE_EVENT_PREFIX = `settitle-slider__`;
export const SETONCLOSE_EVENT_PREFIX = `setonclose-slider__`;

function getWidgetClassNames(collapsed: boolean) {
  return classNames(`${SLIDER_CLASS}`, {
    [`${SLIDER_CLASS}--collapsed`]: collapsed,
  });
}

export interface BasePortalSliderProps {
  htmlContent?: string;
  sliderId?: string;
  onClose?: () => void;
  onOpenInNewWindow?: () => void;
  onMounted?: (sliderBody: HTMLElement) => void;
  closeOnOutsideClick?: boolean;
  openPortalSlider?: boolean;
  sliderTitle?: string;
}

export interface PortalSliderProps extends BasePortalSliderProps {
  children?: React.ReactNode;
  closed?: boolean;
  applyTabbing?: boolean;
}

export function PortalSlider({
  onClose,
  onOpenInNewWindow,
  onMounted,
  children,
  htmlContent,
  sliderId,
  closed,
  closeOnOutsideClick,
  openPortalSlider = true,
  sliderTitle = '',
  applyTabbing = true,
}: PortalSliderProps) {
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [showLayer, setShowLayer] = useState<boolean>(openPortalSlider);
  const [title, setTitle] = useState<string>(sliderTitle);
  const [closeListener, setCloseListener] = useState<() => void>(() => {});
  // id - immutable
  const [id] = useState<string>(sliderId || generateGuid());
  // set z-index only 1 time for dialog existing
  const [zIndex] = useState(getMaxZIndex());
  const tabUtil = useRef<TabManagementUtil>();

  // close animation befor closing
  const close = useCallback(() => {
    setCollapsed(true);
    setTimeout(() => {
      closeListener && closeListener();
      onClose && onClose();
    }, 400);
  }, [onClose, closeListener]);

  // for closing from react via parameter `closed`
  useEffect(() => {
    if (closed) {
      close();
    }
  }, [close, closed]);

  const layerActions: DomEvent[] = [
    {
      name: 'click',
      action: useCallback(() => {
        closeOnOutsideClick && close();
      }, [close, closeOnOutsideClick]),
    },
  ];

  const onCreate = useCallback(
    (node: HTMLDivElement): void => {
      node?.focus?.();
      if (htmlContent && !!node?.appendChild) {
        const slotHtml = document.createRange().createContextualFragment(htmlContent); // create a 'tiny' document and parse the html string
        node.innerHTML = '';
        node.appendChild(slotHtml);
      }

      if (node) {
        onMounted && onMounted(node);
        tabUtil.current = ensureTabManagement(node, applyTabbing);
      }
      return;
    },
    [applyTabbing, htmlContent, onMounted]
  );

  const togglePortalSlider = useCallback(
    (e: any) => {
      if (e?.detail?.show) {
        setShowLayer(true);
        // tell it to focus the first element again, if it's being reopened:
        tabUtil.current && tabUtil.current.focusFirstElement(applyTabbing);
        setTimeout(() => {
          setCollapsed(false);
        }, 1);
      } else {
        setCollapsed(true);
        setTimeout(() => {
          setShowLayer(false);
        }, 1);
      }
    },
    [applyTabbing]
  );

  const setPortalSliderTitle = useCallback((e: any) => {
    setTitle(e?.detail?.title);
  }, []);

  const setPortalSliderOnClose = useCallback(
    (e: any) => {
      setCloseListener(() => e?.detail?.onClose);
    },
    [setCloseListener]
  );

  const keydownEventHandler = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          close();
          break;
      }
    },
    [close]
  );

  // close animation after rendering
  useLayoutEffect(() => {
    window.addEventListener('keydown', keydownEventHandler);
    window.addEventListener(`${CLOSE_EVENT_PREFIX}${id}`, close);
    // to do: Reducer
    window.addEventListener(`${TOGGLE_EVENT_PREFIX}${id}`, togglePortalSlider);
    window.addEventListener(`${SETTITLE_EVENT_PREFIX}${id}`, setPortalSliderTitle);
    window.addEventListener(`${SETONCLOSE_EVENT_PREFIX}${id}`, setPortalSliderOnClose);

    if (collapsed && openPortalSlider) {
      setTimeout(() => {
        setCollapsed(false);
      }, 1);
    }

    return () => {
      window.removeEventListener('keydown', keydownEventHandler);
      window.removeEventListener(`${CLOSE_EVENT_PREFIX}${id}`, close);
      window.removeEventListener(`${TOGGLE_EVENT_PREFIX}${id}`, togglePortalSlider);
      window.removeEventListener(`${SETTITLE_EVENT_PREFIX}${id}`, setPortalSliderTitle);
      window.removeEventListener(`${SETONCLOSE_EVENT_PREFIX}${id}`, setPortalSliderOnClose);

      setCollapsed(true);
    };

    // component unmout effect for closing animation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Slider
      widgetClassName={`${getWidgetClassNames(collapsed)} ui-front`}
      layerClassName={`${SLIDER_CLASS}-layer ui-front`}
      id={id}
      zIndex={zIndex}
      showLayer={showLayer}
      layerActions={layerActions}
    >
      <SliderHeader onClose={close} onOpenInNewWindowClick={onOpenInNewWindow} title={title} />
      <div className={`dpc-portal-slider__body`} ref={onCreate}>
        {children}
      </div>
    </Slider>
  );
}
